import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyPageModule } from '@vendasta/galaxy/page';
import { InboxService } from '../../../../../core/src';
import { getBackUrl } from '../constants';
import { WebchatFormComponent } from '../webchat-form/webchat-form.component';
import { firstValueFrom, map } from 'rxjs';
import { BrandingV2Service } from '@galaxy/partner';
import { ACCOUNT_GROUP_ID_TOKEN, COMPANY_NAME_TOKEN, PARTNER_ID_TOKEN } from '../../../../../core/src/lib/tokens';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'inbox-new-webchat',
  imports: [CommonModule, MatCardModule, GalaxyPageModule, TranslateModule, MatIconModule, WebchatFormComponent],
  templateUrl: './new-webchat.component.html',
  styleUrls: ['./new-webchat.component.scss'],
})
export class NewWebchatComponent implements OnInit {
  private readonly inboxService = inject(InboxService);
  protected readonly isBusinessApp = this.inboxService.isBusinessApp;
  protected readonly webChatForm = WebchatFormComponent.webChatFormGroupFactory();
  protected readonly knowledgeForm = WebchatFormComponent.knowledgeFormGroupFactory();
  protected readonly backUrl = getBackUrl(this.isBusinessApp, 2);

  private readonly accountGroupId = toSignal(inject(ACCOUNT_GROUP_ID_TOKEN));
  private readonly partnerId$ = inject(PARTNER_ID_TOKEN);
  private readonly brandingService = inject(BrandingV2Service);
  private readonly accountGroupName$ = inject(COMPANY_NAME_TOKEN);

  private readonly creating = signal(false);
  protected readonly isLoading = computed(() => this.creating());

  ngOnInit(): void {
    this.setDefaultWidgetName();
  }

  protected creatingWebChat(v: boolean): void {
    this.creating.set(v);
  }

  private async setDefaultWidgetName(): Promise<void> {
    if (this.webChatForm.get('name')?.value) {
      return;
    }
    const defaultName = await this.getDefaultWidgetName();
    this.webChatForm.get('name')?.setValue(defaultName);
  }

  private async getDefaultWidgetName(): Promise<string> {
    const fallbackName = 'Website Chat Widget';
    if (this.accountGroupId()) {
      return firstValueFrom(this.accountGroupName$.pipe(map((agName: string) => agName || fallbackName)));
    } else {
      const partnerId = await firstValueFrom(this.partnerId$);
      try {
        const branding = await firstValueFrom(this.brandingService.getBranding(partnerId));
        return branding?.name || fallbackName;
      } catch {
        return fallbackName;
      }
    }
  }
}
